/* The front door: is my data healthy, what needs me, and what do I do next.
 *
 * **Redesigned in the Overview's own surface language** — soft panels, an indigo mark, a pastel family
 * and a choreographed arrival — and scoped under `.lq-ov` so not one other screen changes. The shell
 * around it stays the product's, square and teal; the difference is deliberate and confined to the
 * one page that is opened to be glanced at rather than worked in.
 *
 * **Every figure on this page comes from an endpoint some other screen already reads.** Seven reads,
 * no new API surface and no per-dataset fan-out, which is the constraint that shaped the page more
 * than any visual decision: a dashboard is exactly where an N+1 gets written, because the tempting
 * card is "average score across your estate" and the only way to build it from a per-dataset API is
 * one request per dataset. `GET /v1/scoring/estate` aggregates it server-side instead.
 *
 * **A figure that could not be read says so in words, and never renders a zero.** "Nothing is waiting
 * for you" and "we could not find out" are different facts; a zero for the second is the defect this
 * product records at four screens. Unmeasured is not failing either — it is hatched, not red.
 *
 * **The worklist reports its own blind spots.** Four sources feed "Needs attention"; if one fails the
 * list is *shorter* while looking complete, which reads as "less needs me" — the inversion of what
 * happened. `unreadableSources` names the ones that failed, above the cards.
 *
 * **Motion is one GSAP timeline in the order the reference builds its dashboard** — header, figures,
 * actions, chart, the rail — compressed to about a second, because this is a page opened many times a
 * day and a long entrance is a tax on every visit. The contents of each panel (the chart drawing, a
 * figure counting, a list settling) wait for their panel through `Cues`, and none of it runs for a
 * reader who asked for reduced motion.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useIdentity } from '@/entities/session';
import { useFindings } from '@/features/anomaly';
import { useDatasetNames } from '@/features/catalog-browser';
import { useReviewQueue } from '@/features/classification-review';
import { ageInMinutes, useIncidentQueue } from '@/features/incidents';
import { useFailureWindow } from '@/features/rules';
import { usePlanQueue } from '@/features/remediation';
import { useEstate } from '@/features/scoring';
import { SCREEN_PATHS } from '@/shared/routes';
import { CueProvider, useEntrance } from '@/shared/ui/motion';
import type { Cues } from '@/shared/ui/motion';

import {
  countSeverities,
  describeAge,
  nameSubject,
  needsAttention,
  orderAttention,
  severityRank,
  unreadableSources,
} from '../model/overview';
import type { AttentionItem, SeverityCounts } from '../model/overview';
import { ActivityPanel } from './ActivityPanel';
import { AttentionPanel } from './AttentionPanel';
import { CopilotPanel } from './CopilotPanel';
import { CoveragePanel } from './CoveragePanel';
import { AlertIcon, ClockIcon, FailIcon, GaugeIcon, LayersIcon } from './icons';
import { Kpi, Strip } from './OverviewParts';
import type { KpiReading, KpiTone } from './OverviewParts';
import { QualityTrend } from './QualityTrend';
import { QuickActions } from './QuickActions';

/* How many rules the server's concentration figure covers.
 *
 * **A mirror of a server value, named once.** `top_rule_violations` is the sum over the three worst
 * rules and the API does not publish the three, because the number is a property of the aggregate
 * rather than of the tenant. So the client has to state it to write the sentence — and stating it
 * once, named, beside a note saying where the real value lives, is the honest version of a duplicated
 * constant. Publishing the count on the response is the better fix, recorded rather than done because
 * it changes a shipped contract for one word of copy. */
const TOP_RULES_COUNTED = 3;

/* The window the label names before the response arrives. A successful response carries
 * `window_hours` and that is what gets rendered, so the two cannot disagree about a figure on screen. */
const DEFAULT_FAILURE_HOURS = 24;

/* The entrance, in seconds from the page's first frame, in the order the reference builds it. One
 * table, so the sequence is readable in one place and the cues are derived from it rather than
 * restated. */
const BEAT = {
  head: 0,
  kpis: 0.12,
  actions: 0.28,
  trend: 0.4,
  rail: 0.48,
  coverage: 0.58,
} as const;

/* How far into its panel's fade a panel's contents may begin: late enough that the panel is mostly
 * there, early enough that the two read as one arrival rather than two. */
const SETTLE = 0.22;

/* Where each kind of attention card sends a reader. A map rather than a chain of conditionals so a new
 * `AttentionKind` is a compile error here rather than a card whose action goes nowhere. */
const TARGET: Readonly<Record<AttentionItem['kind'], string>> = {
  incident: SCREEN_PATHS.incidents,
  anomaly: SCREEN_PATHS.anomalies,
  review: SCREEN_PATHS.review,
  approval: SCREEN_PATHS.remediation,
};

const formatScore = (n: number): string => n.toFixed(1);
/* Rounded because a count-up passes through fractions; the resting value is always a whole number. */
const formatCount = (n: number): string => Math.round(n).toLocaleString();

/** The estate's movement over the server's window, as a chip: a glyph, a sign in words for a screen
 * reader, and the magnitude. A change that rounds to nothing says "no change" rather than "▲ 0.0",
 * which would claim a direction the figure does not have. */
function Change({
  delta,
  days,
}: {
  readonly delta: number;
  readonly days: number;
}): ReactElement {
  const { t } = useTranslation();
  const flat = Math.abs(delta) < 0.05;
  const up = delta > 0;
  return (
    <>
      {flat ? (
        <span className="lq-ov-delta lq-ov-delta--flat">{t('home.kpi.noChange')}</span>
      ) : (
        <span
          className={up ? 'lq-ov-delta lq-ov-delta--up' : 'lq-ov-delta lq-ov-delta--down'}
        >
          <span aria-hidden="true">{up ? '▲' : '▼'}</span>
          <span className="lq-visually-hidden">
            {up ? t('home.kpi.rose') : t('home.kpi.fell')}
          </span>
          {Math.abs(delta).toFixed(1)}
        </span>
      )}
      <span className="lq-ov-kpi__aside">{t('home.kpi.overDays', { days })}</span>
    </>
  );
}

/** The three severities under the incident figure, the worst first and emphasised only when there is
 * something to emphasise — a red "no critical" would paint the good news as bad. Info is named only
 * when present, which is exactly when the strip above draws it. */
function SeveritySplit({ counts }: { readonly counts: SeverityCounts }): ReactElement {
  const { t } = useTranslation();
  return (
    <span className="lq-ov-kpi__split">
      <span className={counts.critical > 0 ? 'lq-ov-kpi__lead' : 'lq-ov-kpi__part'}>
        {t('home.kpi.critical', { count: counts.critical })}
      </span>
      <span className="lq-ov-kpi__part">
        {t('home.kpi.warning', { count: counts.warning })}
      </span>
      {counts.info > 0 ? (
        <span className="lq-ov-kpi__part">
          {t('home.kpi.info', { count: counts.info })}
        </span>
      ) : null}
    </span>
  );
}

export function HomeScreen(): ReactElement {
  const { t } = useTranslation();

  const incidents = useIncidentQueue();
  const anomalies = useFindings();
  const review = useReviewQueue();
  const plans = usePlanQueue();
  const estate = useEstate();
  const failures = useFailureWindow();
  /* Shared with every screen that turns a dataset id into a name, so the dashboard cannot disagree
     with the catalogue about what a table is called. */
  const names = useDatasetNames();
  /* The query the shell's own chip reads, so it costs no request: React Query dedupes on the key. */
  const identity = useIdentity();

  /* **`now` is the moment the queue was fetched, not the moment of this render.** A fresh `new Date()`
     per render would give every age string a new value on any unrelated state change; reading
     `dataUpdatedAt` as the value makes the claim exact — these ages are as of the last successful
     read, which is what the reader is looking at. */
  const now = useMemo(() => new Date(incidents.dataUpdatedAt), [incidents.dataUpdatedAt]);

  /* When each beat of the entrance lands, from the first render. See `Cues` for why a deep component
     needs to know, and `BEAT` for the order. */
  const [cues] = useState<Cues>(() => {
    const start = performance.now();
    const at = (seconds: number): number => start + seconds * 1000;
    return {
      kpi: at(BEAT.kpis + SETTLE),
      trend: at(BEAT.trend + SETTLE),
      rail: at(BEAT.rail + SETTLE),
      coverage: at(BEAT.coverage + SETTLE),
    };
  });

  const page = useEntrance<HTMLDivElement>((root, timeline) => {
    const all = (selector: string): Element[] =>
      Array.from(root.querySelectorAll(selector));
    timeline
      .from(all('[data-ov="head"] > *'), { y: 8, opacity: 0, stagger: 0.06 }, BEAT.head)
      .from(
        all('[data-ov="kpi"]'),
        { y: 12, scale: 0.985, opacity: 0, stagger: 0.06, duration: 0.55 },
        BEAT.kpis,
      )
      .from(all('.lq-ov-quick'), { y: 12, opacity: 0 }, BEAT.actions)
      .from(
        all('[data-ov="action"]'),
        { y: 8, opacity: 0, stagger: 0.05, duration: 0.45 },
        BEAT.actions + 0.06,
      )
      .from(all('.lq-ov-trend'), { y: 14, opacity: 0, duration: 0.55 }, BEAT.trend)
      .from(
        all('.lq-ov-attn, .lq-ov-feed, .lq-ov-copilot'),
        { y: 14, opacity: 0, stagger: 0.08, duration: 0.55 },
        BEAT.rail,
      )
      .from(all('.lq-ov-coverage'), { y: 14, opacity: 0, duration: 0.55 }, BEAT.coverage);
  });

  /* ---------------------------------------------------------------- the merged worklist
     Built from four reads and ordered in the model, so the ordering is testable without a browser and
     the cards cannot disagree with the KPI about what is worst. */
  const items: AttentionItem[] = [];
  if (incidents.isSuccess) {
    for (const incident of incidents.data) {
      if (incident.severity === 'info') continue;
      const naming = nameSubject(
        incident.kind,
        incident.subject_id,
        names.isSuccess ? names.data : null,
      );
      const age = describeAge(ageInMinutes(incident, now));
      items.push({
        id: `incident:${incident.id}`,
        kind: 'incident',
        /* **Never the raw id.** A name when the catalogue can supply one, otherwise a sentence naming
           why it cannot — see `nameSubject`. */
        subject: naming.kind === 'named' ? naming.name : t(naming.reasonKey),
        named: naming.kind === 'named',
        /* The API's own sentence. `summary` is built from column names and counts and never carries a
           value from the dataset — see `Incident.summary`. */
        why: incident.summary,
        since: t(`home.attention.age.${age.unit}`, { count: age.count }),
        rank: severityRank(incident.severity),
        tone: incident.severity === 'critical' ? 'danger' : 'warn',
      });
    }
  }
  if (anomalies.isSuccess) {
    for (const finding of anomalies.data) {
      if (finding.verdict !== 'anomalous') continue;
      /* An anomaly's subject is always a dataset, so it is named through the same path — passing a
         monitor kind is how that is said. */
      const naming = nameSubject(
        'volume_anomaly',
        finding.dataset_id,
        names.isSuccess ? names.data : null,
      );
      items.push({
        id: `anomaly:${finding.id}`,
        kind: 'anomaly',
        subject: naming.kind === 'named' ? naming.name : t(naming.reasonKey),
        named: naming.kind === 'named',
        why: t('home.attention.whyAnomaly', { signal: finding.signal }),
        since: t('home.attention.age.unknown'),
        rank: 0,
        tone: 'warn',
      });
    }
  }
  if (review.isSuccess && review.data.length > 0) {
    /* One card for the queue rather than one per dataset: "review 40 classifications" is one decision
       a steward makes once, and forty cards would bury every incident beneath an afternoon's work. */
    items.push({
      id: 'review:queue',
      kind: 'review',
      subject: t('home.attention.reviewSubject', { count: review.data.length }),
      named: true,
      why: t('home.attention.whyReview'),
      since: t('home.attention.age.unknown'),
      rank: 0,
      tone: 'neutral',
    });
  }
  if (plans.isSuccess && plans.data.length > 0) {
    items.push({
      id: 'approval:queue',
      kind: 'approval',
      subject: t('home.attention.approvalSubject', { count: plans.data.length }),
      named: true,
      why: t('home.attention.whyApproval'),
      since: t('home.attention.age.unknown'),
      rank: 0,
      tone: 'neutral',
    });
  }
  const ordered = orderAttention(items);

  const blind = unreadableSources([
    { failed: incidents.isError, label: t('home.attention.sourceIncidents') },
    { failed: anomalies.isError, label: t('home.attention.sourceAnomalies') },
    { failed: review.isError, label: t('home.attention.sourceReview') },
    { failed: plans.isError, label: t('home.attention.sourceApprovals') },
  ]);

  const settled =
    !incidents.isPending && !anomalies.isPending && !review.isPending && !plans.isPending;

  /* ---------------------------------------------------------------- the four figures */

  const unreadable = t('home.kpi.unreadable');

  /* Estate quality: one aggregate the database computes, `null` when nothing is scored — its own
     branch rather than a zero, because zero is "everything failed" and this is "nobody has looked". */
  const estateReading: KpiReading = estate.isPending
    ? { kind: 'loading' }
    : estate.isError
      ? { kind: 'absent', word: unreadable, why: estate.error.message }
      : estate.data.value === null
        ? { kind: 'absent', word: t('home.kpi.estateUnscored') }
        : { kind: 'value', value: estate.data.value };

  /* Datasets measured: `scored` from the scoring aggregate, the total from the catalogue walk.
     **Clamped to the catalogue**, because a score survives its dataset being retired while the total
     counts live datasets — a tenant that retired its measured tables would otherwise read "3 of 0". */
  const total = names.isSuccess ? names.data.byId.size : null;
  const scored =
    estate.isSuccess && total !== null ? Math.min(estate.data.scored, total) : null;
  const measuredReading: KpiReading =
    estate.isPending || names.isPending
      ? { kind: 'loading' }
      : total === null || scored === null
        ? {
            kind: 'absent',
            word: unreadable,
            why: (estate.error ?? names.error)?.message,
          }
        : total === 0
          ? {
              kind: 'absent',
              word: t('home.kpi.measuredNoCatalogue'),
              why: t('home.kpi.measuredNoCatalogueWhy'),
            }
          : { kind: 'value', value: scored };

  const severities = incidents.isSuccess ? countSeverities(incidents.data) : null;
  const severityTotal =
    severities === null ? 0 : severities.critical + severities.warning + severities.info;
  const incidentReading: KpiReading = incidents.isPending
    ? { kind: 'loading' }
    : severities === null
      ? { kind: 'absent', word: unreadable, why: incidents.error?.message }
      : { kind: 'value', value: needsAttention(severities) };
  const incidentTone: KpiTone =
    severities === null
      ? 'neutral'
      : severities.critical > 0
        ? 'coral'
        : severities.warning > 0
          ? 'amber'
          : 'mint';

  const failureReading: KpiReading = failures.isPending
    ? { kind: 'loading' }
    : failures.isError
      ? { kind: 'absent', word: unreadable, why: failures.error.message }
      : { kind: 'value', value: failures.data.violations };
  const failureTone: KpiTone = failures.isSuccess
    ? failures.data.violations > 0
      ? 'coral'
      : 'mint'
    : 'neutral';

  /* How old the headline is. From the *estate* read, because that is the figure the page is named
     for, and quoting the freshest of several reads would date it by whichever refetched last. */
  const asOf = describeAge(
    estate.isSuccess
      ? Math.max(0, Math.floor((Date.now() - estate.dataUpdatedAt) / 60_000))
      : null,
  );
  /* The tenant's own name, which `GET /v1/auth/me` carries. `tenant_id` stays absent on hard rule 1;
     a name addresses nothing. `null` renders nothing rather than a placeholder. */
  const organisation = identity.data?.organisation_name ?? null;

  return (
    <CueProvider value={cues}>
      <div ref={page} className="lq-ov" data-motion="own">
        <header className="lq-ov-head" data-ov="head">
          <div className="lq-ov-head__titles">
            <h1 className="lq-ov-head__title">{t('home.title')}</h1>
            <p className="lq-ov-head__lede">{t('home.lede')}</p>
          </div>
          {organisation === null && !estate.isSuccess ? null : (
            <ul className="lq-ov-head__meta">
              {organisation === null ? null : (
                <li className="lq-ov-chip">
                  <span className="lq-ov-chip__label">{t('home.tenant')}</span>
                  <strong className="lq-ov-chip__value">{organisation}</strong>
                </li>
              )}
              {estate.isSuccess ? (
                <li className="lq-ov-chip">
                  <ClockIcon className="lq-ov-icon" />
                  <span>{t('home.updated', { unit: asOf.unit, count: asOf.count })}</span>
                </li>
              ) : null}
            </ul>
          )}
        </header>

        <section className="lq-ov-kpis" aria-label={t('home.kpi.region')}>
          <Kpi
            icon={<GaugeIcon className="lq-ov-icon" />}
            tone={estate.isSuccess ? 'indigo' : 'neutral'}
            label={t('home.kpi.estate')}
            help={t('help.metric.estateScore')}
            to={SCREEN_PATHS.scores}
            reading={estateReading}
            format={formatScore}
            unit={t('home.kpi.outOf100')}
            foot={
              !estate.isSuccess ? undefined : estate.data.value === null ? (
                <span className="lq-ov-kpi__aside">{t('home.kpi.estateStart')}</span>
              ) : estate.data.delta === null ? (
                /* No earlier measurement. A flat 0.0 would report stability nobody can vouch for. */
                <span className="lq-ov-kpi__aside">{t('home.kpi.noBaseline')}</span>
              ) : (
                <Change delta={estate.data.delta} days={estate.data.window_days} />
              )
            }
          />

          <Kpi
            icon={<LayersIcon className="lq-ov-icon" />}
            tone={measuredReading.kind === 'value' ? 'blue' : 'neutral'}
            label={t('home.kpi.measured')}
            help={t('help.metric.datasetsMeasured')}
            to={SCREEN_PATHS.datasets}
            reading={measuredReading}
            format={formatCount}
            unit={total === null ? undefined : t('home.kpi.measuredOf', { total })}
            picture={
              total === null || scored === null || total === 0 ? undefined : (
                /* **The unmeasured tail is hatched, not tinted**: a paler band reads as a second,
                   worse measurement, and a dataset with no score is not a bad score. */
                <Strip
                  hatchRemainder
                  segments={[{ id: 'scored', share: scored / total, tone: 'mark' }]}
                  label={t('home.kpi.measuredBar', { scored, total })}
                />
              )
            }
            foot={
              total === null || scored === null || total === 0 ? undefined : (
                <span className="lq-ov-kpi__aside">
                  {t('home.kpi.measuredRest', { count: total - scored })}
                </span>
              )
            }
          />

          <Kpi
            icon={<AlertIcon className="lq-ov-icon" />}
            tone={incidentTone}
            label={t('home.kpi.incidents')}
            help={t('help.metric.openIncidents')}
            to={SCREEN_PATHS.incidents}
            reading={incidentReading}
            format={formatCount}
            /* **The severity split with no hatched remainder**: every open incident is critical,
               warning or info, so these bands are the whole. Info is drawn though the headline leaves
               it out — the strip shows the queue's shape, the figure counts what wants a person. */
            picture={
              severities === null || severityTotal === 0 ? undefined : (
                <Strip
                  segments={[
                    {
                      id: 'critical',
                      share: severities.critical / severityTotal,
                      tone: 'coral',
                    },
                    {
                      id: 'warning',
                      share: severities.warning / severityTotal,
                      tone: 'amber',
                    },
                    {
                      id: 'info',
                      share: severities.info / severityTotal,
                      tone: 'neutral',
                    },
                  ]}
                  label={t('home.attention.severityBar', {
                    critical: severities.critical,
                    warning: severities.warning,
                    info: severities.info,
                  })}
                />
              )
            }
            foot={severities === null ? undefined : <SeveritySplit counts={severities} />}
          />

          <Kpi
            icon={<FailIcon className="lq-ov-icon" />}
            tone={failureTone}
            label={t('home.kpi.failures', {
              hours: failures.data?.window_hours ?? DEFAULT_FAILURE_HOURS,
            })}
            help={t('help.metric.ruleFailures')}
            to={SCREEN_PATHS.executions}
            reading={failureReading}
            format={formatCount}
            /* **Concentration, not a per-day bar chart, and that is a real constraint.** The window
               returns six scalars and no series, so bars would be invented numbers. What the figures
               the server does return draw honestly is how much of the day's damage is three rules —
               shown only when the server judges it concentrated, since on a tenant with two rules the
               top three are the whole and a full bar says something true and useless. */
            picture={
              failures.isSuccess &&
              failures.data.violations > 0 &&
              failures.data.concentrated ? (
                <Strip
                  segments={[
                    {
                      id: 'top',
                      share: failures.data.top_rule_violations / failures.data.violations,
                      tone: 'coral',
                    },
                    {
                      id: 'rest',
                      share:
                        Math.max(
                          0,
                          failures.data.violations - failures.data.top_rule_violations,
                        ) / failures.data.violations,
                      tone: 'neutral',
                    },
                  ]}
                  label={t('home.kpi.failuresBar', {
                    share: failures.data.top_rule_violations,
                    total: failures.data.violations,
                    rules: TOP_RULES_COUNTED,
                  })}
                />
              ) : undefined
            }
            foot={
              !failures.isSuccess ? undefined : (
                <span className="lq-ov-kpi__aside">
                  {failures.data.violations === 0
                    ? t('home.kpi.failuresNone')
                    : failures.data.concentrated
                      ? t('home.kpi.failuresTop', {
                          share: failures.data.top_rule_violations,
                          rules: TOP_RULES_COUNTED,
                        })
                      : t('home.kpi.failuresSpread', {
                          datasets: failures.data.datasets,
                        })}
                </span>
              )
            }
          />
        </section>

        {/* **Three groups, not two columns, so the order is right at every width.** On a wide screen
            the main column sits beside the rail, with "Needs attention" at the rail's head; below that
            everything is one column, and the worklist comes straight after the figures — the thing a
            reader most needs on a phone should not sit under a chart and a coverage table. */}
        <div className="lq-ov-grid">
          <div className="lq-ov-lead">
            <AttentionPanel
              items={ordered}
              targets={TARGET}
              blind={blind}
              settled={settled}
              unknownAge={t('home.attention.age.unknown')}
            />
          </div>
          <div className="lq-ov-main">
            <QuickActions />
            <QualityTrend />
            <CoveragePanel />
          </div>
          <div className="lq-ov-side">
            <ActivityPanel now={now} />
            <CopilotPanel busy={!settled} waiting={ordered.length > 0} />
          </div>
        </div>
      </div>
    </CueProvider>
  );
}
